import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface HelpState {
  topic: string | null;
  isOpen: boolean;
}

interface HelpAPI {
  state: HelpState;
  setTopic: (id: string) => void;
  open: (topicId?: string) => void;
  close: () => void;
  toggle: () => void;
}

const HelpCtx = createContext<HelpAPI | null>(null);

export function useHelp(): HelpAPI {
  const ctx = useContext(HelpCtx);
  if (!ctx) throw new Error('useHelp must be used within HelpProvider');
  return ctx;
}

export const HelpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<HelpState>({ topic: null, isOpen: false });

  const setTopic = useCallback((id: string) => {
    setState((current) => ({ ...current, topic: id, isOpen: true }));
  }, []);

  const open = useCallback((topicId?: string) => {
    setState((current) => ({
      ...current,
      isOpen: true,
      ...(topicId ? { topic: topicId } : {}),
    }));
  }, []);

  const close = useCallback(() => {
    setState((current) => ({ ...current, isOpen: false }));
  }, []);

  const toggle = useCallback(() => {
    setState((current) => ({ ...current, isOpen: !current.isOpen }));
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const element = event.target as HTMLElement | null;
      const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(element?.tagName || '') || element?.isContentEditable;

      if (event.key === '?' && !isInput && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        toggle();
      } else if (event.key === 'Escape') {
        close();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [close, toggle]);

  return (
    <HelpCtx.Provider value={{ state, setTopic, open, close, toggle }}>
      {children}
    </HelpCtx.Provider>
  );
};
